// The draft recap (DRAFT_RECAP.md): a completed draft in, grades per team,
// steals and reaches against the average draft position, and lines with
// personality out. Values and ADP come from the host; no sport names here.

import type { DraftPick, DraftState } from './engine';

export interface RecapContext {
  /** A player's value over replacement, the same basis as the rankings. */
  value: (playerId: string) => number;
  /**
   * Average draft position as an overall pick number; null for a player the
   * board has no place for (one it no longer ranks), who is then neither a
   * steal nor a reach: there was nothing to be early or late against.
   */
  adp: (playerId: string) => number | null;
  position: (playerId: string) => string;
  playerName: (playerId: string) => string;
  ownerName: (teamId: string) => string;
  teamName: (teamId: string) => string;
  /**
   * An auction's (AUCTION.md §7): each player's value in its money, and how
   * many dollars from it a price has to be to be a bargain or a splurge. Each
   * sale is then judged against its value rather than its place, `adp` is that
   * value and `delta` the dollars it went under it.
   */
  auction?: { worth: (playerId: string) => number; margin: number };
  /**
   * What a bot's draft style came to, as what follows its name ("took 4
   * Vikings, and no Packers", `styleRecap`), from the players it drafted; null
   * for a person's team or a style that did not show. Absent, no team has one.
   */
  style?: (teamId: string, playerIds: readonly string[]) => string | null;
}

export type Grade = 'A' | 'A-' | 'B+' | 'B' | 'B-' | 'C+' | 'C';

export interface PickNote {
  overall: number;
  round: number;
  slot: number;
  teamId: string;
  playerId: string;
  adp: number;
  /** Picks later than the room expected (positive is a steal); in an auction, dollars under value. */
  delta: number;
  value: number;
  /** What an auction's winner paid; absent for a pick. */
  price?: number;
  /** A pick the board had no place for (`RecapContext.adp` null): judged neither way, never a team's best. */
  unranked?: boolean;
  line: string;
}

export interface TeamGrade {
  teamId: string;
  rank: number;
  grade: Grade;
  totalValue: number;
  line: string;
  bestPick: PickNote | null;
  steals: number;
  reaches: number;
  positions: { position: string; count: number }[];
  /** A bot's style in its picks: "Grandma Dot took 4 Vikings, and no Packers." Null otherwise. */
  style: string | null;
}

export interface DraftRecap {
  headline: string;
  teams: TeamGrade[];
  steals: PickNote[];
  reaches: PickNote[];
}

/** A pick this many spots past its ADP is a steal; this many before, a reach. */
const STEAL_MARGIN = 6;

function hash(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

function pickOne<T>(key: string, options: readonly T[]): T {
  return options[hash(key) % options.length]!;
}

function fill(template: string, d: Record<string, string | number>): string {
  return template
    .replace(/\{(\w+)\}/g, (_, k: string) => String(d[k] ?? ''))
    .replace(/\s{2,}/g, ' ');
}

function gradeFor(rank: number, count: number): Grade {
  if (count <= 1) return 'A';
  const q = (rank - 1) / (count - 1);
  if (q <= 0.08) return 'A';
  if (q <= 0.25) return 'A-';
  if (q <= 0.42) return 'B+';
  if (q <= 0.58) return 'B';
  if (q <= 0.75) return 'B-';
  if (q <= 0.92) return 'C+';
  return 'C';
}

const TEAM_LINES: Record<Grade, string[]> = {
  A: [
    '{owner} drafted like the answer key was open: {best} at {bestOverall}, and the best roster in the room.',
    'Nobody left with more than {owner}. {best} at {bestOverall} was the pick of the night.',
  ],
  'A-': [
    '{owner} drafted a contender. Best value: {best} at {bestOverall}.',
    'A strong night for {owner}. {best} at {bestOverall} will be talked about in December.',
  ],
  'B+': [
    'Solid work from {owner}. {best} at {bestOverall} was the steal; nothing embarrassing.',
    '{owner} did the sensible thing every round, and {best} at {bestOverall} did the fun one.',
  ],
  B: [
    "{owner}'s draft was fine, which is the nicest thing anyone will say about it.",
    '{owner} drafted a team. It has players on it. {best} at {bestOverall} was the good one.',
  ],
  'B-': [
    '{owner} took a few players a round early. {best} saves it.',
    'A reach or two from {owner}, and {best} at {bestOverall} to make up for them.',
  ],
  'C+': [
    // `{reachTimes}` and `{reachCount}` rather than `{reaches}` twice: the
    // first version of these read "Rose reached 1 times" against a real
    // recap, which is the kind of sentence that makes a whole page look
    // machine-written. A template with a number in it needs the plural
    // decided where the number is, not where the sentence is.
    '{owner} reached {reachTimes}. The group chat noticed.',
    '{owner} had a plan. The plan had {reachCount} in it.',
  ],
  C: [
    '{owner} drafted with heart. The scoreboard will explain.',
    'Somebody has to finish last in the grades. {owner} volunteered.',
  ],
};

/** For the grades whose lines count reaches, what to say when there were none. */
const NO_REACH_LINES: Partial<Record<Grade, string[]>> = {
  'B-': [
    '{owner} never reached and still landed here. {best} at {bestOverall} is the argument.',
    'No reaches from {owner}, no steals either. {best} at {bestOverall} was the pick.',
  ],
  'C+': [
    '{owner} played it straight all night. The board just did not agree with the plan.',
    'Not one reach from {owner}, which makes this grade harder to explain and easier to fix.',
  ],
};

// NO "HIM" OR "HIS" IN A PICK'S LINE: a pick can be a team's defense, which
// the recap called "him" (the walk of 2026-10-26).
const STEAL_LINES = [
  '{player} to {owner} at {overall}, {delta} picks after the room expected.',
  '{owner} sat and waited and got {player} at {overall}, a pick the room expected by {adp}.',
];
const REACH_LINES = [
  '{owner} took {player} at {overall}, {delta} picks before anyone else would have.',
  '{player} at {overall} for {owner}, where the room expected {adp}. Bold.',
];
/** A pick the board had no place for: said, not judged. */
const UNRANKED_LINE = '{player} to {owner} at {overall}.';
const HEADLINES = [
  '{top} won the draft; {bottom} will argue.',
  'The draft, graded: {top} on top, {bottom} on the phone with the commissioner.',
  '{top} drafted the best roster in the room. {bottom} drafted a story.',
];

const BARGAIN_LINES = [
  '{owner} got {player} for ${price}, ${delta} under value.',
  '{player} went to {owner} for ${price}. The room let ${delta} of value walk out the door.',
];
const SPLURGE_LINES = [
  '{owner} paid ${price} for {player}, ${delta} over value.',
  '{player} for ${price}, to {owner}, valued at ${value}. Bold.',
];

/** A sale, judged against the player's value: dollars under it are a bargain. */
function saleNote(pick: DraftPick, ctx: RecapContext): PickNote {
  const worth = Math.round(ctx.auction!.worth(pick.playerId));
  const price = pick.price ?? 0;
  const delta = worth - price;
  return {
    overall: pick.overall,
    round: pick.round,
    slot: pick.slot,
    teamId: pick.teamId,
    playerId: pick.playerId,
    adp: worth,
    delta,
    value: ctx.value(pick.playerId),
    price,
    line: fill(
      pickOne(`${pick.teamId}:${pick.playerId}`, delta >= 0 ? BARGAIN_LINES : SPLURGE_LINES),
      {
        player: ctx.playerName(pick.playerId),
        owner: ctx.ownerName(pick.teamId),
        price,
        delta: Math.abs(delta),
        value: worth,
      },
    ),
  };
}

function note(pick: DraftPick, ctx: RecapContext, rounds: number): PickNote {
  if (ctx.auction && pick.price !== undefined) return saleNote(pick, ctx);
  const expected = ctx.adp(pick.playerId);
  if (expected === null)
    return {
      overall: pick.overall,
      round: pick.round,
      slot: pick.slot,
      teamId: pick.teamId,
      playerId: pick.playerId,
      adp: pick.overall,
      delta: 0,
      value: ctx.value(pick.playerId),
      unranked: true,
      line: fill(UNRANKED_LINE, {
        player: ctx.playerName(pick.playerId),
        owner: ctx.ownerName(pick.teamId),
        overall: pick.overall,
      }),
    };
  const adp = expected;
  // NOTHING IN THE FINAL ROUND IS A REACH. The last round is where every
  // team takes what is left — a kicker, a defense, a flier — and a player the
  // board expected to go undrafted is, by definition, expected in no round at
  // all. Grading that pick against the end of the draft still called three
  // final-round defenses the league's worst reaches (AUDIT-2026-09-23 RD4),
  // one clamp after the first version had called them −326. A steal in the
  // last round is still a steal.
  const raw = Math.round(pick.overall - adp);
  const delta = pick.round === rounds && raw < 0 ? 0 : raw;
  const template = delta >= 0 ? STEAL_LINES : REACH_LINES;
  return {
    overall: pick.overall,
    round: pick.round,
    slot: pick.slot,
    teamId: pick.teamId,
    playerId: pick.playerId,
    adp: Math.round(adp),
    delta,
    value: ctx.value(pick.playerId),
    line: fill(pickOne(`${pick.teamId}:${pick.playerId}`, template), {
      player: ctx.playerName(pick.playerId),
      owner: ctx.ownerName(pick.teamId),
      overall: pick.overall,
      delta: Math.abs(delta),
      adp: Math.round(adp),
    }),
  };
}

/** The recap for a completed draft; null while it is still going. */
export function draftRecap(draft: DraftState, ctx: RecapContext): DraftRecap | null {
  if (draft.status !== 'complete' || draft.picks.length === 0) return null;
  // THE DRAFT'S OWN CHOICES: a keeper's pick was made by the rules before it
  // began (PLAN-2026-10-27-the-next-season §2.3), so it is never a steal, a
  // reach or part of how well a team drafted.
  const notes = draft.picks.filter((p) => p.by !== 'keeper').map((p) => note(p, ctx, draft.rounds));
  // A STEAL IS PLACES LATE, or in an auction dollars under value.
  const margin = ctx.auction?.margin ?? STEAL_MARGIN;
  const byTeam = new Map<string, PickNote[]>();
  for (const n of notes) byTeam.set(n.teamId, [...(byTeam.get(n.teamId) ?? []), n]);
  const rows = draft.order.map((teamId) => {
    const picks = byTeam.get(teamId) ?? [];
    const totalValue = Math.round(picks.reduce((s, n) => s + n.value, 0) * 10) / 10;
    // AN UNRANKED PICK IS NEVER THE BEST: with nothing to be early or late
    // against, its 0 would beat a team whose every ranked pick came early,
    // and its line call him the steal.
    const judged = picks.some((n) => !n.unranked) ? picks.filter((n) => !n.unranked) : picks;
    const best = [...judged].sort((a, b) => b.delta - a.delta || b.value - a.value)[0] ?? null;
    const counts = new Map<string, number>();
    for (const n of picks) {
      const pos = ctx.position(n.playerId);
      counts.set(pos, (counts.get(pos) ?? 0) + 1);
    }
    return {
      teamId,
      totalValue,
      best,
      steals: picks.filter((n) => n.delta >= margin).length,
      reaches: picks.filter((n) => n.delta <= -margin).length,
      positions: [...counts.entries()]
        .map(([position, count]) => ({ position, count }))
        .sort((a, b) => b.count - a.count || a.position.localeCompare(b.position)),
    };
  });
  rows.sort((a, b) => b.totalValue - a.totalValue);
  const styleLine = (teamId: string): string | null => {
    const said = ctx.style?.(
      teamId,
      (byTeam.get(teamId) ?? []).map((n) => n.playerId),
    );
    return said == null ? null : `${ctx.ownerName(teamId)} ${said}.`;
  };
  const teams: TeamGrade[] = rows.map((r, i) => {
    const grade = gradeFor(i + 1, rows.length);
    return {
      teamId: r.teamId,
      rank: i + 1,
      grade,
      totalValue: r.totalValue,
      // A LINE ABOUT REACHES IS ONLY FOR A TEAM THAT REACHED. "Rose reached
      // 0 times. The group chat noticed." was served for real (RD4 / U11);
      // a template with a count in it needs a branch for zero as much as
      // for one.
      line: fill(
        pickOne(
          `${draft.id}:${r.teamId}:grade`,
          r.reaches === 0 ? (NO_REACH_LINES[grade] ?? TEAM_LINES[grade]) : TEAM_LINES[grade],
        ),
        {
          owner: ctx.ownerName(r.teamId),
          best: r.best ? ctx.playerName(r.best.playerId) : 'nobody',
          // "Bijan Robinson at $23" in an auction, "at 14" in a pick draft.
          bestOverall:
            r.best === null
              ? ''
              : r.best.price !== undefined
                ? `$${String(r.best.price)}`
                : r.best.overall,
          reaches: r.reaches,
          reachTimes: r.reaches === 1 ? 'once' : `${r.reaches} times`,
          reachCount: r.reaches === 1 ? 'one reach' : `${r.reaches} reaches`,
        },
      ),
      bestPick: r.best,
      steals: r.steals,
      reaches: r.reaches,
      positions: r.positions,
      style: styleLine(r.teamId),
    };
  });
  const steals = notes
    .filter((n) => n.delta >= margin)
    .sort((a, b) => b.delta - a.delta)
    .slice(0, 5);
  const reaches = notes
    .filter((n) => n.delta <= -margin)
    .sort((a, b) => a.delta - b.delta)
    .slice(0, 5);
  const top = teams[0]!;
  const bottom = teams[teams.length - 1]!;
  return {
    headline: fill(pickOne(`${draft.id}:headline`, HEADLINES), {
      top: ctx.ownerName(top.teamId),
      bottom: ctx.ownerName(bottom.teamId),
    }),
    teams,
    steals,
    reaches,
  };
}
